import React, { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import AsyncCollectionState from "./components/AsyncCollectionState";
import CommunityFeed from "./components/CommunityFeed";
import { normalizeEventRecord } from "./eventViewModel";
import {
  buildCommunityFeed,
  filterCommunityFeed,
  getAvailableCommunityFeedFilters,
} from "./communityFeed";

export default function Community() {
  const [uploads, setUploads] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [uploadsError, setUploadsError] = useState("");
  const [eventsError, setEventsError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  const uploadsQuery = useMemo(
    () => query(collection(db, "uploads"), where("status", "==", "approved"), orderBy("createdAt", "desc")),
    []
  );
  const eventsQuery = useMemo(
    () => query(collection(db, "events"), where("status", "==", "approved")),
    []
  );

  useEffect(() => {
    setUploadsLoading(true);
    setEventsLoading(true);
    setUploadsError("");
    setEventsError("");

    const uploadsUnsub = onSnapshot(
      uploadsQuery,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUploads(items);
        setUploadsLoading(false);
      },
      (error) => {
        console.error("Unable to load approved community updates", error);
        setUploadsError("Community documents are temporarily unavailable.");
        setUploadsLoading(false);
      }
    );

    const eventsUnsub = onSnapshot(
      eventsQuery,
      (snap) => {
        setEvents(snap.docs.map((d) => normalizeEventRecord(d.id, d.data())));
        setEventsLoading(false);
      },
      (error) => {
        console.error("Unable to load approved community events", error);
        setEventsError("Community events are temporarily unavailable.");
        setEventsLoading(false);
      }
    );

    return () => {
      uploadsUnsub();
      eventsUnsub();
    };
  }, [uploadsQuery, eventsQuery, loadAttempt]);

  const feed = useMemo(() => buildCommunityFeed({ events, uploads }), [events, uploads]);
  const filterOptions = useMemo(() => getAvailableCommunityFeedFilters(feed), [feed]);
  const visibleFeed = useMemo(() => filterCommunityFeed(feed, filter), [feed, filter]);

  useEffect(() => {
    if (!filterOptions.some((option) => option.value === filter)) {
      setFilter("all");
    }
  }, [filter, filterOptions]);

  const loading = uploadsLoading || eventsLoading;
  const allSourcesFailed = Boolean(uploadsError && eventsError);
  const partialError = !allSourcesFailed && (uploadsError || eventsError);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold">Community</h1>
          <p className="text-white/70 mt-1">
            Local events and community updates, brought together in one feed — newest first.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="community-feed-filter" className="text-sm text-white/70">Filter:</label>
          <select
            id="community-feed-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-black/40 border border-white/20 rounded px-3 py-2 text-sm"
          >
            {filterOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {partialError && (
        <div role="status" className="rounded-xl border border-amber-200/20 bg-amber-400/10 p-3 text-sm text-amber-100">
          {partialError} Showing the content that is currently available.
        </div>
      )}

      <AsyncCollectionState
        loading={loading}
        error={allSourcesFailed ? "Community content is temporarily unavailable. Please try again." : ""}
        isEmpty={visibleFeed.length === 0}
        emptyMessage={filter === "all" ? "No approved community content yet. Check back soon." : "No content matches this filter yet."}
        onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
      >
        <CommunityFeed items={visibleFeed} />
      </AsyncCollectionState>
    </div>
  );
}
